// Ubicación en el repo: src/pages/Dashboard.jsx
// Pantalla de Inicio (DECISIONS.md #44) — reemplaza el placeholder
// Home.jsx y el redirect directo a /pacientes. Replica renderDashboard()
// del prototipo (línea ~714): 4 tarjetas numéricas, tendencia de 3/6/12
// meses (citas + ventas) y un bloque de alertas que cambia según el
// módulo activo, más la cartelera de avisos de la sucursal.
import { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getDashboardData } from '../lib/dashboard';
import TrendChartSVG from '../components/TrendChartSVG';
import DonutChartSVG from '../components/DonutChartSVG';
import BranchNotes from '../components/BranchNotes';
import Spinner from '../components/Spinner';

const RANGE_OPTIONS = [3, 6, 12];

function monthLabel(monthStr) {
  const [y, m] = monthStr.split('-');
  const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('es-CL', {
    month: 'long',
    year: 'numeric',
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export default function Dashboard() {
  const { activeBranchId, activeBranch, activeModule, professional } = useOutletContext();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [range, setRange] = useState(6);

  useEffect(() => {
    if (!activeBranchId || !activeModule) return;
    setLoading(true);
    setError('');
    getDashboardData({ branchId: activeBranchId, module: activeModule })
      .then(setData)
      .catch((err) => setError(err.message || 'No se pudo cargar el dashboard.'))
      .finally(() => setLoading(false));
  }, [activeBranchId, activeModule]);

  if (loading || !data) {
    return (
      <div className="patients-page">
        <h1>Inicio</h1>
        {error ? <p className="auth-error">{error}</p> : <Spinner />}
      </div>
    );
  }

  const n = Math.min(range, data.months.length);
  const months = data.months.slice(-n);
  const series = [
    { label: 'Citas', color: 'var(--primary)', data: data.citasTrend.slice(-n) },
    { label: data.alerts.type === 'ORL' ? 'Ventas de audífonos' : 'Ventas de lentes', color: 'var(--accent)', data: data.ventasTrend.slice(-n) },
  ];

  return (
    <div className="patients-page dashboard-page">
      <div className="patients-page__toolbar">
        <h1>Inicio</h1>
        <p className="text-muted">
          {activeBranch?.name} · {monthLabel(data.months[data.months.length - 1])}
        </p>
      </div>

      <BranchNotes branchId={activeBranchId} professional={professional} />

      <div className="card-panel dashboard-metrics">
        <h3>Resumen del mes</h3>
        <table className="patients-table dashboard-metrics-table">
          <thead>
            <tr>
              <th>Indicador</th>
              <th>Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Citas este mes</td>
              <td>{data.aptsThisMonthCount}</td>
            </tr>
            <tr>
              <td>{data.secondCard.label}</td>
              <td>{data.secondCard.value}</td>
            </tr>
            <tr>
              <td>{data.thirdCard.label}</td>
              <td>{data.thirdCard.value}</td>
            </tr>
            <tr>
              <td>Citas hoy</td>
              <td>{data.aptsTodayCount}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="dashboard-grid">
        <div className="card-panel">
          <div className="dashboard-trend-header">
            <h3>Tendencia</h3>
            <div className="view-toggle">
              {RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={range === opt ? 'active' : ''}
                  onClick={() => setRange(opt)}
                >
                  {opt} meses
                </button>
              ))}
            </div>
          </div>
          <div className="legend">
            {series.map((s) => (
              <span key={s.label}>
                <span className="dot-square" style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
          <TrendChartSVG months={months} series={series} />
        </div>

        <div className="card-panel">
          <h3>Estado de citas del mes</h3>
          <div className="donut-panel">
            <DonutChartSVG segments={data.statusDistribution} />
            <ul className="donut-legend">
              {data.statusDistribution.map((s) => (
                <li key={s.label}>
                  <span className="dot-square" style={{ background: s.color }} />
                  {s.label}
                  <strong>{s.value}</strong>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {data.alerts.type === 'ORL' && data.alerts.lowStock.length > 0 && (
          <div className="card-panel">
            <h3>Stock bajo</h3>
            {data.alerts.lowStock.map((c) => (
              <div key={c.id} className="apt-item">
                <span className={`badge ${c.stock <= 0 ? 'badge-danger' : 'badge-warn'}`}>{c.stock} en stock</span>
                <div className="apt-info">
                  <div className="name">
                    {c.brand} {c.model}
                  </div>
                  <div className="meta">
                    {c.type} · mín. {c.stock_min}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
